import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { muiTheme } from 'app/themes/materialUi';
import { withStyles } from '@material-ui/core';

import AddIcon from '@material-ui/icons/Add';
import { Tab, Tabs, Button, CircularProgress } from '@mic3/platform-ui';
import { Responsive, WidthProvider } from 'react-grid-layout';
import genUuidv4 from 'uuid/v4';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import ErrorBoundary from 'app/components/atoms/ErrorBoundary/ErrorBoundary';
import StaticWidget from './StaticWidget';
import AliveWidget from './AliveWidget';
import Loader from 'app/components/atoms/Loader/Loader';
import HeaderBar from 'app/components/molecules/HeaderBar/HeaderBar';
import DotMenu from 'app/components/molecules/DotMenu/DotMenu';
import { useDispatch, useSelector } from 'react-redux';
import AddListWidgetModal from './AddListWidgetModal';
import ListWidget from './ListWidget';
import {
    BREAKPOINTS,
    COLUMNS,
    generateResponsiveLayout,
    getBreakpoint,
    findEmptySpace,
    getFallbackPosition,
    getMenuItemByName,
    widgetTypes,
    getAddWidgetMenu,
} from '../dashboardUtils';
import { loadDashboardEntities, updateDashbaordEntity } from 'store/actions/dashboard/dashboardActions';
import { typeMap } from 'app/config/typesConfig';
import AddWidgetModal from './AddWidgetModal';
import { AddDashboardModal } from '../AddDashboardModal';
import { CreateNewWidget, DashboardCalendar, DashboardIFrame, LinksWidget, MapWidgetPreview } from './DashboardWidgets';
import { closeEntitySidebar, openEntitySidebar } from 'store/actions/entities/entitySidebarActions';
import { isEmpty } from 'app/utils/utils';
import { isVisibleTab } from 'app/utils/entity/entityUtils';

import TasksByPriorityWidget from 'app/containers/LandingPage/Widgets/TasksByPriorityWidget';
import TasksByDueDateWidget from 'app/containers/LandingPage/Widgets/TasksByDueDateWidget';
import MapsWidget from 'app/containers/LandingPage/Widgets/MapsWidget';
import LastUpdatedTasksWidget from './LastUpdatedTasksWidget';
import TasksByCreatedDateWidget from './TasksByCreatedDateWidget';
import { getPermissions } from 'app/config/rolesConfig';
import MyAppsWidget from './MyAppsWidget';

const ResponsiveGridLayout = WidthProvider(Responsive);

const DASHBOARD_TYPE = typeMap.dashboard; // WIP: For testing purpose this should be changed to system_dashboard when finalised

const CardItem = styled.div``;

const linkIconColor = muiTheme.colors.appNav.linkIconColor;
const GridWidgetWrapperStyled = styled.div`
    padding: 32px 8px !important;
    background: ${({ theme }) => theme.material.colors.background.default};
    .react-grid-item > .react-resizable-handle::after {
        content: '';
        width: 20px;
        height: 20px;
        display: block;
        position: absolute;
        bottom: 5px;
        right: 5px;
        background-image: ${`
            radial-gradient(${linkIconColor} 50%, transparent 20%),
            radial-gradient(${linkIconColor} 50%, transparent 20%),
            radial-gradient(${linkIconColor} 50%, transparent 20%),
            radial-gradient(${linkIconColor} 50%, transparent 20%),
            radial-gradient(${linkIconColor} 50%, transparent 20%),
            radial-gradient(${linkIconColor} 50%, transparent 20%)
        `};
        background-size: 4px 4px;
        background-position: calc(100% - 0px) calc(100% - 0px), calc(100% - 6px) calc(100% - 0px), calc(100% - 12px) calc(100% - 0px),
            calc(100% - 0px) calc(100% - 6px), calc(100% - 6px) calc(100% - 6px), calc(100% - 0px) calc(100% - 12px);
        background-repeat: no-repeat;
        border-right: none;
        border-bottom: none;
    }
    .react-resizable-handle {
        background-image: none;
    }
`;

const DashboardList = styled.div`
    display: flex;
    gap: 16px;
    width: 100%;
`;

const StyledTab = styled(Tab)`
    &.MuiTab-textColorInherit {
        opacity: 1 !important;
    }
    &:hover {
        background-color: ${({ theme }) => theme.material.colors.background.hover} !important;
        border-radius: 4px;
        transition: background-color 0.3s ease;
    }
    &:hover svg,
    &:hover span {
        color: ${({ theme }) => theme.material.colors.text.landingActiveButton} !important;
        transition: background-color 0.3s ease;
    }
`;

const ResponsiveGridLayoutStyled = styled(ResponsiveGridLayout)`
    & .react-grid-item.react-grid-placeholder {
        background: ${({ theme }) => theme.material.colors.background.paper};
    }
`;

const UserSpecificWidgets = (props) => {
    const { item } = props;
    switch (item?.selectedWidget) {
        case 'tasksByDueDate':
            return <TasksByDueDateWidget {...props} />;
        case 'tasksByCreatedDate':
            return <TasksByCreatedDateWidget {...props} />;
        case 'tasksByPriority':
            return <TasksByPriorityWidget {...props} />;
        case 'lastUpdatedTasks':
            return <LastUpdatedTasksWidget {...props} />;
        case 'userMaps':
            return <MapsWidget {...props} />;
        default:
            return null;
    }
};

const widgetComponents = {
    [widgetTypes.alive]: AliveWidget,
    [widgetTypes.iframe]: DashboardIFrame,
    [widgetTypes.module]: StaticWidget,
    [widgetTypes.calendar]: DashboardCalendar,
    [widgetTypes.map]: MapWidgetPreview,
    [widgetTypes.list]: ListWidget,
    [widgetTypes.user]: UserSpecificWidgets,
    [widgetTypes.link]: LinksWidget,
    [widgetTypes.userApps]: MyAppsWidget,
};

/* Dashboard
* Show Save button should be enabled if there is any layout or widget changed
* Fix the widget header character so if widget header is too long then ui do not disturb
* Add date selection in Calendar

* Widgets
* Dupliate
*/

const styles = (theme) => ({
    tabLabel: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '150px',
    },
    tabSelected: {
        color: `${theme.colors.text.landingActiveButton} !important`,
    },
    tabRoot: {
        color: theme.colors.appHeader.iconColor,
        opacity: 1,
    },
    scrollButtons: {
        color: theme.colors.appHeader.iconColor,
        opacity: 1,
    },
});

const LandingPageWidgets = ({ classes, widgetsKey }) => {
    const isSidebarOpen = useSelector((state) => state.sidebar.isOpen);
    const windowWidth = useSelector((state) => state.global.window.width);
    const isMobile = useSelector((state) => state.global.isMobile);
    const userProfile = useSelector((state) => state.user.profile);
    const { data, isLoading: loadingDashbaord } = useSelector((state) => state.dashboard.dashboardEntities);
    const [selectedDashboardAction, setSelectedDashboardAction] = useState('');
    const [selectedDashboardId, setSelectedDashboardId] = useState(null);
    const [widgets, setWidgets] = useState([]);
    const [listWidgetData, setListWidgetData] = useState(null);
    const [modalWidget, setModalWidget] = useState(null);
    const [isAddListWidgetModalOpen, toggleAddListWidgetModal] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modelType, setModalType] = useState(null);
    const [isAddDashModalOpen, toggleAddDashModal] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [gridLayout, setGridLayout] = useState({});
    const dispatch = useDispatch();

    const { isAdmin } = userProfile || {};

    const permissionsSet = new Set(userProfile?.permissions || []);
    const canAddDashboard = isAdmin || permissionsSet.has('dashboard.add');

    const dashboards = useMemo(() => {
        return data?.records || [];
    }, [data]);

    useEffect(() => {
        dispatch(loadDashboardEntities());
    }, [dispatch, widgetsKey]);

    useEffect(() => {
        const dashboard = dashboards[activeTab] || {};
        if (dashboard?.id) {
            setSelectedDashboardId(dashboard.id);
        }
    }, [activeTab, dashboards]);

    const onLayoutChange = (originalLayout) => {
        const newLayout = originalLayout.map(({ i, w, h, x, y }) => ({ i, w, h, x, y }));
        const breakpoint = getBreakpoint(windowWidth);
        setGridLayout({ ...gridLayout, [breakpoint]: newLayout });
    };

    useEffect(() => {
        const respLayout = generateResponsiveLayout(widgets);
        setGridLayout(respLayout);
    }, [windowWidth, isSidebarOpen]); // eslint-disable-line

    useEffect(() => {
        if (!selectedDashboardId) return;
        const dashboard = dashboards[activeTab];
        const dashboardDef = dashboard?.primary?.[`${DASHBOARD_TYPE}/definition`] || {};
        const { layout = null } = dashboardDef;
        let { widgets = [] } = dashboardDef;
        widgets = widgets.filter((w) => w.name !== 'addNew').map((w) => ({ ...w, id: w?.id || genUuidv4() })); // If there is already addNew component saved then we would remove it
        if (layout) {
            setGridLayout(layout); // If the layout is already saved then we will load the existing layout otherwise we will generate a new layout
        } else {
            const respLayout = generateResponsiveLayout(widgets);
            setGridLayout(respLayout);
        }
        setWidgets(widgets);
    }, [selectedDashboardId, dashboards, activeTab]);

    const handleTabChange = (e, value) => {
        setActiveTab(value);
        dispatch(closeEntitySidebar());
    };

    const handleAddMenuClick = (item) => {
        const matchedItem = getMenuItemByName(item?.name, userProfile);
        if (!matchedItem) return;
        const { type } = matchedItem || {};
        switch (type) {
            case widgetTypes.list:
                toggleAddListWidgetModal(true);
                break;
            case widgetTypes.alive: {
                const widget = { id: genUuidv4(), type, data: null };
                handleAddWidget(widget);
                break;
            }
            default:
                setModalType(type);
                setIsModalOpen(true);
        }
    };

    const handleAddWidget = (item, isDuplicate) => {
        if (!item) return;
        const newItem = { ...item };

        const updatedRespLayout = Object.keys(BREAKPOINTS).reduce((respLayout, breakpoint) => {
            const currentLayout = gridLayout[breakpoint] || [];
            const columns = COLUMNS[breakpoint];
            let newWidgetLayout = null;
            // Find empty space
            const emptySpace = findEmptySpace(currentLayout, columns);

            // Determine fallback position if no empty space is found
            const { x, y } = emptySpace || getFallbackPosition(currentLayout, columns);

            // Create the new widget layout item
            newWidgetLayout = { i: newItem.id, x, y, w: 1, h: 1 };
            if (isDuplicate) {
                const id = genUuidv4();
                // ASSIGNING THE NEW ID TO ITEM AS WE GENERATED IN THE LAYOUT TO SYNC BETWEEN LAYOUT AND WIDGET
                newItem.id = id;
                newWidgetLayout.i = id;
            }
            // Update the widgets and layout
            respLayout[breakpoint] = [...currentLayout, newWidgetLayout];
            return respLayout;
        }, {});
        const updatedWidgets = [...widgets.filter((w) => w.name !== 'addNew'), newItem];
        setWidgets(updatedWidgets);
        setGridLayout(updatedRespLayout);
    };

    const onSave = async () => {
        const dashboard = dashboards.find((d) => d.id === selectedDashboardId);
        if (!dashboard) return;
        const { id, type } = dashboard;
        const updatedDashboard = {
            id,
            type,
            primary: {
                [`${DASHBOARD_TYPE}/definition`]: {
                    widgets,
                    layout: gridLayout, // saving the layout
                },
            },
        };
        await dispatch(updateDashbaordEntity(updatedDashboard));
        dispatch(loadDashboardEntities());
    };

    const removeWidget = (widgetId) => {
        const updatedWidgets = widgets.filter((widget) => widget.id !== widgetId);
        setWidgets(updatedWidgets);

        const newLayout = JSON.parse(JSON.stringify(gridLayout));

        // Iterate over each layout type (e.g., 'lg', 'sm', 'xl', etc.)
        for (const layoutType in newLayout) {
            if (newLayout.hasOwnProperty(layoutType)) {
                // Filter out the widget with the matching ID
                newLayout[layoutType] = newLayout[layoutType].filter((widget) => widget.i !== widgetId);
            }
        }
        setGridLayout(newLayout);
    };

    const handleEditWidget = (widget) => {
        if (!widget) return;
        const { type } = widget || {};
        switch (type) {
            case 'list':
                setListWidgetData(widget);
                toggleAddListWidgetModal(true);
                return;
            default:
                setModalWidget(widget);
                setIsModalOpen(true);
                setModalType(type);
                return;
        }
    };

    const handleWidgetMenuClick = (menu, widgetId) => {
        if (!menu) return;
        const widget = widgets.find((w) => w.id === widgetId);
        switch (menu) {
            case 'Remove widget':
                removeWidget(widgetId);
                return;
            case 'Edit widget':
                handleEditWidget(widget);
                return;
            case 'Duplicate widget':
                handleAddWidget(widget, true);
                return;
        }
    };

    const updateWidget = (widget) => {
        if (!widget || !widgets?.length) return;
        const newWidgets = widgets.map((w) => {
            if (w?.id === widget?.id) {
                return { ...w, ...widget, data: { ...(w.data || {}), ...(widget?.data || {}) } };
            }
            return { ...w };
        });
        setWidgets(newWidgets);
    };

    const onModalClose = () => {
        toggleAddListWidgetModal(false);
        setListWidgetData(null);
    };

    const handleDashboardActions = (menu) => {
        if (menu === 'Sharing') {
            dispatch(openEntitySidebar({ id: selectedDashboardId, type: typeMap.dashboard, title: 'Sharing' }));
            return;
        }
        if (menu === 'Rename') {
            toggleAddDashModal(true);
        }
        setSelectedDashboardAction(menu);
    };
    if (isEmpty(gridLayout)) return null;

    const selectedDashboard = dashboards?.find((d) => d?.id === selectedDashboardId);
    const permissions = getPermissions(selectedDashboard?.role);

    return (
        <>
            <HeaderBar
                leftStyles={{ maxWidth: isMobile ? '100%' : '80%' }}
                isForward={true}
                left={
                    <DashboardList>
                        <Tabs
                            variant='scrollable'
                            value={activeTab}
                            onChange={handleTabChange}
                            classes={{ scrollButtons: classes.scrollButtons }}
                        >
                            {dashboards.map((d, index) => {
                                return (
                                    <Tab
                                        key={index}
                                        title={d.name}
                                        label={<span className={classes.tabLabel}>{d.name}</span>}
                                        classes={{
                                            root: classes.tabRoot, // Apply custom styles to the `root`
                                            selected: classes.tabSelected, // Apply custom styles to the selected tab
                                        }}
                                    />
                                );
                            })}
                        </Tabs>
                        {canAddDashboard ? (
                            <StyledTab
                                key='addNewDashboard'
                                onClick={() => {
                                    toggleAddDashModal(true);
                                    setSelectedDashboardAction('');
                                }}
                                label={
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <AddIcon style={{ marginRight: 8, color: muiTheme.colors.text.caption }} />
                                        <span style={{ color: muiTheme.colors.text.caption }}>Create New</span>
                                    </div>
                                }
                            />
                        ) : null}
                    </DashboardList>
                }
                right={
                    permissions?.canEdit ? (
                        <>
                            {loadingDashbaord ? (
                                <CircularProgress size={25} />
                            ) : (
                                <Button variant='contained' onClick={onSave} style={{ width: '68px', height: '37px' }}>
                                    Save
                                </Button>
                            )}
                            <DotMenu
                                onItemClick={(menu) => handleAddMenuClick({ name: menu })}
                                icon='plus'
                                items={getAddWidgetMenu(userProfile)}
                                tooltipTitle='Add Widget'
                            />
                            <DotMenu
                                onItemClick={handleDashboardActions}
                                items={[
                                    { name: 'Rename', icon: 'pencil' },
                                    isVisibleTab('Sharing') && { name: 'Sharing', icon: 'share-variant' },
                                ].filter(Boolean)}
                                tooltipTitle='Actions'
                            />
                        </>
                    ) : (
                        <DotMenu
                            onItemClick={handleDashboardActions}
                            items={[isVisibleTab('Sharing') && { name: 'Sharing', icon: 'share-variant' }].filter(Boolean)}
                            tooltipTitle='More'
                        />
                    )
                }
            />
            {loadingDashbaord ? <Loader absolute /> : null}
            {widgets?.length ? (
                <GridWidgetWrapperStyled>
                    <ResponsiveGridLayoutStyled
                        className='layout'
                        layouts={gridLayout}
                        breakpoints={BREAKPOINTS}
                        cols={COLUMNS}
                        rowHeight={360}
                        width={windowWidth < 480 ? window.innerWidth - 20 : window.innerWidth - 70} // Grid width
                        isDraggable={permissions?.canEdit && !isSidebarOpen && windowWidth > 480}
                        isResizable={permissions?.canEdit && !isSidebarOpen && windowWidth > 480}
                        onLayoutChange={onLayoutChange}
                        draggableHandle='.drag-handle'
                        margin={[16, 16]}
                        isBounded
                        containerPadding={[0, 0]}
                    >
                        {widgets.map((widget) => {
                            const { id, type, data } = widget || {};
                            const { avatarProps, ...rest } = data || {};
                            const Component = widgetComponents[type];
                            if (!Component) {
                                console.error(`Dashboard system widget, Component name not valid!`); // eslint-disable-line
                                return null;
                            }
                            const compProps = { id, type, permissions };
                            return (
                                <CardItem key={id}>
                                    <ErrorBoundary>
                                        <Component
                                            key={`${id}_${widgetsKey}`}
                                            item={rest}
                                            avatarProps={avatarProps}
                                            content={<h1>Content</h1>}
                                            menuItemClick={(menu) => handleWidgetMenuClick(menu, id)}
                                            updateWidget={updateWidget}
                                            {...compProps}
                                        />
                                    </ErrorBoundary>
                                </CardItem>
                            );
                        })}
                    </ResponsiveGridLayoutStyled>
                </GridWidgetWrapperStyled>
            ) : (
                <CreateNewWidget
                    onAddWidget={handleAddMenuClick}
                    toggleAddDashModal={toggleAddDashModal}
                    selectedDashboardId={selectedDashboardId}
                />
            )}
            {isAddListWidgetModalOpen ? (
                <AddListWidgetModal
                    widgetData={listWidgetData}
                    onClose={onModalClose}
                    onAddWidget={handleAddWidget}
                    updateWidget={updateWidget}
                />
            ) : null}
            {isModalOpen ? (
                <AddWidgetModal
                    widget={modalWidget}
                    type={modelType}
                    onAddWidget={handleAddWidget}
                    onUpdateWidget={updateWidget}
                    userProfile={userProfile}
                    onClose={() => {
                        setIsModalOpen(false);
                        setModalType(null);
                        setModalWidget(null);
                    }}
                />
            ) : null}
            {isAddDashModalOpen ? (
                <AddDashboardModal
                    onClose={() => toggleAddDashModal(false)}
                    selectedDashboard={dashboards?.find((d) => d?.id === selectedDashboardId)}
                    action={selectedDashboardAction}
                    setActiveTab={setActiveTab}
                />
            ) : null}
        </>
    );
};

export default withStyles(styles)(LandingPageWidgets);
